import React from 'react';
import { Link, NavLink, matchPath, useLocation, useSearchParams } from 'react-router-dom';

import {
  useCiAdminAuth,
  useCiAdminBasePath,
} from '../../contexts/CiAdminAuthContext';
import CiSiteFilterBar from './CiSiteFilterBar';

const CI_SECTIONS = [
  { path: 'sites', icon: 'bi-globe2', label: 'Sites' },
  { path: 'customers', icon: 'bi-building', label: 'Customers' },
  { path: 'users', icon: 'bi-person-badge', label: 'Users' },
  { path: 'shipto-addresses', icon: 'bi-geo-alt', label: 'Ship-To Addresses' },
  { path: 'contacts', icon: 'bi-person-lines-fill', label: 'Contacts' },
  { path: 'user-groups', icon: 'bi-people', label: 'User groups' },
  { path: 'settings', icon: 'bi-sliders', label: 'Settings' },
  {
    path: 'platform-users',
    icon: 'bi-shield-check',
    label: 'Platform users',
    superAdminOnly: true,
  },
  {
    path: 'customer-import',
    icon: 'bi-cloud-upload',
    label: 'Customer import',
    superAdminOnly: true,
  },
  {
    path: 'learned-associations',
    icon: 'bi-link-45deg',
    label: 'Learned Associations',
    superAdminOnly: true,
  },
];

/**
 * Section navigation for the Customer Identity module. Lives inside the hero
 * so users can jump between areas without relying on the sidebar.
 *
 * Preserves `site_id` (and `customer_id` when present) in the query string
 * when switching sections so storefront scope and Users↔Groups context stay aligned.
 *
 * Links are relative to the mount point (`/cia` or `/customer-identity`).
 */
function buildSectionTo(basePath, section, siteId, customerId) {
  if (section.path === 'users' && customerId) {
    const q = new URLSearchParams();
    if (siteId) q.set('site_id', siteId);
    q.set('tab', 'users');
    return `${basePath}/customers/${customerId}?${q}`;
  }
  const q = new URLSearchParams();
  if (siteId) q.set('site_id', siteId);
  if (customerId) q.set('customer_id', customerId);
  const qs = q.toString();
  return `${basePath}/${section.path}${qs ? `?${qs}` : ''}`;
}

function isCiSectionActive(section, basePath, location) {
  if (section.path === 'users') {
    if (matchPath({ path: `${basePath}/users`, end: true }, location.pathname)) {
      return true;
    }
    const detail = matchPath(
      { path: `${basePath}/customers/:customerId`, end: true },
      location.pathname,
    );
    if (detail) {
      const tab = new URLSearchParams(location.search).get('tab') || 'users';
      return tab === 'users';
    }
    return false;
  }
  if (section.path === 'customers') {
    return Boolean(
      matchPath({ path: `${basePath}/customers`, end: false }, location.pathname),
    );
  }
  return Boolean(
    matchPath({ path: `${basePath}/${section.path}`, end: false }, location.pathname),
  );
}

export function CiSectionNav() {
  const { isSuperAdmin } = useCiAdminAuth();
  const basePath = useCiAdminBasePath();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const siteId = searchParams.get('site_id');
  const customerId = searchParams.get('customer_id');
  const sections = CI_SECTIONS.filter(
    (section) => !section.superAdminOnly || isSuperAdmin,
  );
  return (
    <nav className="ci-section-nav" aria-label="Customer Identity sections">
      {sections.map((section) => (
        <NavLink
          key={section.path}
          to={buildSectionTo(basePath, section, siteId, customerId)}
          className={() =>
            `ci-section-nav-link${
              isCiSectionActive(section, basePath, location) ? ' is-active' : ''
            }`
          }
        >
          <i className={`bi ${section.icon}`} aria-hidden="true" />
          <span>{section.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * @param {{
 *   title: string,
 *   subtitle?: string,
 *   breadcrumbs?: { label: string, to?: string }[],
 *   actions?: React.ReactNode,
 *   sectionNav?: boolean,
 *   tabs?: React.ReactNode,
 *   children: React.ReactNode,
 * }} props
 */
export default function CiPageChrome({
  title,
  subtitle,
  breadcrumbs = [],
  actions = null,
  sectionNav = true,
  tabs = null,
  children,
}) {
  return (
    <div className="ci-admin-page flex-grow-1 d-flex flex-column min-vh-0">
      <div className="ci-hero">
        <div className="ci-hero-main px-3 px-md-4 pt-2 pb-1">
          {breadcrumbs.length > 0 && (
            <nav className="ci-hero-breadcrumb" aria-label="Breadcrumb">
              {breadcrumbs.map((crumb, i) => (
                <span key={`${crumb.label}-${i}`}>
                  {i > 0 && <span className="ci-hero-crumb-sep mx-1">/</span>}
                  {crumb.to ? (
                    <Link to={crumb.to}>{crumb.label}</Link>
                  ) : (
                    <span className="ci-hero-crumb-current">{crumb.label}</span>
                  )}
                </span>
              ))}
            </nav>
          )}
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
            <div>
              <h1 className="ci-hero-title">{title}</h1>
              {subtitle ? <p className="ci-hero-subtitle">{subtitle}</p> : null}
            </div>
            {actions ? <div className="d-flex flex-wrap gap-2 align-items-center">{actions}</div> : null}
          </div>
        </div>
        <CiSiteFilterBar />
        {sectionNav ? <CiSectionNav /> : null}
        {tabs ? <div className="ci-tabs-wrap">{tabs}</div> : null}
      </div>
      <div className="ci-body flex-grow-1">{children}</div>
    </div>
  );
}
